import { HowToAnswer } from "./HowToAnswer";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";

/**
 * FrequencyBlock — behavioural recall over the last N weeks, one row per
 * activity, with a PER-ROW scale (the choices for "hours on a screen" are
 * not the choices for "evenings you had to work").
 *
 * This is the corroboration engine. Each row emits a numeric value the
 * caller pairs with a stated preference; disagreement above threshold
 * flips the slot to low-confidence and keeps the revealed value.
 *
 * No "It depends" bucket. If they can't remember, the safer default is
 * whichever option matches "not much" for their scale.
 */

export interface FrequencyLevel {
  id: string;
  label: string;
  /** 0..100 — this row's contribution to its slot. */
  value: number;
}

export interface FrequencyRow {
  id: string;
  question: string;
  levels: FrequencyLevel[];
}

export interface FrequencyBlockProps {
  rows: FrequencyRow[];
  onComplete: (answers: Record<string, { levelId: string; value: number }>) => void;
  ctaLabel?: string;
}

export function FrequencyBlock({ rows, onComplete, ctaLabel = "Continue" }: FrequencyBlockProps) {
  const [answers, setAnswers] = useState<Record<string, { levelId: string; value: number }>>({});
  const done = useMemo(() => rows.every((r) => answers[r.id]), [rows, answers]);

  return (
    <div className="space-y-3">
      <HowToAnswer>For each row, tap how often this is true for you.</HowToAnswer>
      {rows.map((row, i) => (
        <motion.div
          key={row.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.04, duration: 0.22 }}
          className="rounded-2xl border border-border bg-card p-4 md:p-5"
        >
          <div className="text-base md:text-lg text-foreground/90 mb-3">{row.question}</div>
          <div
            role="radiogroup"
            aria-label={row.question}
            className={`grid gap-2`}
            style={{ gridTemplateColumns: `repeat(${row.levels.length}, minmax(0, 1fr))` }}
          >
            {row.levels.map((lvl) => {
              const active = answers[row.id]?.levelId === lvl.id;
              return (
                <button
                  key={lvl.id}
                  role="radio"
                  aria-checked={active}
                  onClick={() => setAnswers((a) => ({ ...a, [row.id]: { levelId: lvl.id, value: lvl.value } }))}
                  className={[
                    "min-h-[44px] rounded-xl text-xs md:text-sm border transition px-2 py-2",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border hover:border-primary/50",
                  ].join(" ")}
                >
                  {lvl.label}
                </button>
              );
            })}
          </div>
        </motion.div>
      ))}
      <div className="flex justify-end pt-3">
        <button
          onClick={() => done && onComplete(answers)}
          disabled={!done}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            disabled:opacity-40 disabled:pointer-events-none
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}
